/**
 * GolfGame Component — Main Container
 *
 * Orchestrates the full Golf card game lifecycle:
 * - Game mode selection → engine initialization → gameplay → round transitions → game end
 * - Single-player: AI moves after player turn, AI thinking indicator
 * - Local multiplayer: turn transitions with TurnTransition component
 * - Integrates with platform services: auth, session, notifications, error boundary
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { GolfEngine } from '../GolfEngine';
import { GolfAI } from '../GolfAI';
import {
  GolfGameSettings,
  GolfMove,
  GridPosition,
  GolfDifficulty,
  Card,
} from '../types';
import GolfBoard from './GolfBoard';
import GolfGameModeSelector from './GolfGameModeSelector';
import Scorecard from './Scorecard';
import TurnTransition from './TurnTransition';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { AISpeedSlider } from '../../components/AISpeedSlider';
import { UniversalGameLayout, UniversalGameLayoutConfig } from '../../components/UniversalGameLayout';
import { useAuthentication } from '../../hooks/useAuthentication';
import { useNotifications } from '../../hooks/useNotifications';
import { GameSessionManager } from '../../services/GameSessionManager';
import { NotificationType } from '../../services/NotificationService';
import './GolfGame.css';

export interface GolfGameProps {
  onExit?: () => void;
  initialRoomCode?: string;
}

type GameView = 'mode-select' | 'playing' | 'round-summary' | 'game-over' | 'turn-transition';

const GolfGameInner: React.FC<GolfGameProps> = ({ onExit, initialRoomCode }) => {
  // Core game state
  const [engine] = useState(() => new GolfEngine());
  const [ai, setAi] = useState<GolfAI | null>(null);
  const [settings, setSettings] = useState<GolfGameSettings | null>(null);
  const [view, setView] = useState<GameView>('mode-select');
  const [isAIThinking, setIsAIThinking] = useState(false);
  const [moveCount, setMoveCount] = useState(0);
  const [gameSessionId, setGameSessionId] = useState<string | null>(null);

  // AI speed control
  const [aiSpeedDelay, setAiSpeedDelay] = useState(500);
  const aiSpeedDelayRef = useRef(500);

  // Turn transition state (local multiplayer)
  const [transitionPlayerIndex, setTransitionPlayerIndex] = useState(0);

  // Platform integrations
  const { isAuthenticated, user } = useAuthentication();
  const { addNotification } = useNotifications();

  // Handle deep link room code
  useEffect(() => {
    if (initialRoomCode) {
      // Auto-join room via deep link
      handleJoinPrivateGame(initialRoomCode);
    }
  }, [initialRoomCode]);

  // AI speed change handler
  const handleAiSpeedChange = useCallback((delay: number) => {
    setAiSpeedDelay(delay);
    aiSpeedDelayRef.current = delay;
  }, []);

  // Start a new game with given settings
  const handleStartGame = useCallback((gameSettings: GolfGameSettings) => {
    setSettings(gameSettings);

    const playerCount = gameSettings.playerCount;
    const playerNames = gameSettings.playerNames || generateDefaultNames(playerCount, gameSettings.mode);

    engine.initializeGame(playerCount, playerNames);

    if (gameSettings.mode === 'singleplayer' && gameSettings.aiDifficulty) {
      setAi(new GolfAI(gameSettings.aiDifficulty));
    }

    // Track game session
    const sessionId = GameSessionManager.getInstance().startGameSession('golf-card-game');
    setGameSessionId(sessionId);

    setView('playing');
    setMoveCount(prev => prev + 1);
  }, [engine]);

  // Handle joining a private game room
  const handleJoinPrivateGame = useCallback(async (roomCode: string) => {
    // Online multiplayer join — placeholder for MultiplayerGolfService integration
    addNotification({
      type: NotificationType.INFO,
      title: 'Joining Room',
      message: `Joining room ${roomCode}...`,
    });
    // TODO: Integrate with MultiplayerGolfService in task 20
  }, [addNotification]);

  // Handle player move
  const handleMove = useCallback((move: GolfMove) => {
    setMoveCount(prev => prev + 1);

    const gameState = engine.getGameState();

    // Check if round ended
    if (gameState.roundPhase === 'round-complete') {
      if (engine.isGameOver()) {
        setView('game-over');
        if (gameSessionId) {
          GameSessionManager.getInstance().endGameSession(gameSessionId, true);
        }
        addNotification({
          type: NotificationType.SUCCESS,
          title: 'Game Complete',
          message: 'Game complete! Check the final scorecard.',
        });
      } else {
        setView('round-summary');
        addNotification({
          type: NotificationType.INFO,
          title: 'Round Complete',
          message: `Round ${gameState.currentRound} complete!`,
        });
      }
      return;
    }

    // Single-player: trigger AI turn
    if (settings?.mode === 'singleplayer' && ai) {
      const newState = engine.getGameState();
      if (newState.currentPlayerIndex !== 0 && newState.roundPhase !== 'round-complete') {
        triggerAITurn();
      }
    }

    // Local multiplayer: show turn transition
    if (settings?.mode === 'local-multiplayer') {
      const newState = engine.getGameState();
      const prevPlayer = gameState.currentPlayerIndex;
      const nextPlayer = newState.currentPlayerIndex;

      // Only show transition if player actually changed and game is still active
      if (prevPlayer !== nextPlayer && newState.roundPhase !== 'round-complete') {
        setTransitionPlayerIndex(nextPlayer);
        setView('turn-transition');
      }
    }
  }, [engine, settings, ai, addNotification]);

  // Handle initial reveal
  const handleInitialReveal = useCallback((positions: GridPosition[]) => {
    // Apply reveals to engine
    for (const pos of positions) {
      engine.revealInitialCard(engine.getGameState().currentPlayerIndex === 0 ? 0 : engine.getGameState().currentPlayerIndex, pos);
    }
    setMoveCount(prev => prev + 1);

    const gameState = engine.getGameState();

    // Single-player: AI does initial reveal
    if (settings?.mode === 'singleplayer' && ai && gameState.roundPhase === 'initial-reveal') {
      // Check if AI needs to reveal
      for (let i = 1; i < gameState.playerCount; i++) {
        if (!engine.hasCompletedInitialReveal(i)) {
          setTimeout(() => {
            const aiGrid = engine.getPlayerGrid(i);
            const aiPositions = ai.chooseInitialReveals(aiGrid);
            for (const pos of aiPositions) {
              engine.revealInitialCard(i, pos);
            }
            setMoveCount(prev => prev + 1);
          }, aiSpeedDelayRef.current);
        }
      }
    }

    // Local multiplayer: show transition for next player's reveal
    if (settings?.mode === 'local-multiplayer' && gameState.roundPhase === 'initial-reveal') {
      const currentPlayer = gameState.currentPlayerIndex;
      // Find next player who hasn't revealed
      for (let i = 1; i < gameState.playerCount; i++) {
        const nextIdx = (currentPlayer + i) % gameState.playerCount;
        if (!engine.hasCompletedInitialReveal(nextIdx)) {
          setTransitionPlayerIndex(nextIdx);
          setView('turn-transition');
          break;
        }
      }
    }
  }, [engine, settings, ai]);

  // Trigger AI turn
  const triggerAITurn = useCallback(() => {
    if (!ai) return;

    setIsAIThinking(true);

    setTimeout(() => {
      try {
        const gameState = engine.getGameState();
        const aiPlayerIndex = gameState.currentPlayerIndex;

        // AI draws
        const drawSource = ai.chooseDrawSource(gameState);
        const drawnCard = engine.drawCard(drawSource);

        // AI decides action
        const updatedState = engine.getGameState();
        const action = ai.chooseAction(drawnCard, updatedState);

        if (action.type === 'replace') {
          engine.replaceCard(action.position);
        } else {
          engine.discardDrawnCard();
          // AI flips a card
          const aiGrid = engine.getPlayerGrid(aiPlayerIndex);
          const flipPos = ai.chooseFlipPosition(aiGrid);
          engine.flipCard(flipPos);
        }

        setMoveCount(prev => prev + 1);
        setIsAIThinking(false);

        // Check if round ended after AI move
        const postState = engine.getGameState();
        if (postState.roundPhase === 'round-complete') {
          if (engine.isGameOver()) {
            setView('game-over');
            if (gameSessionId) {
              GameSessionManager.getInstance().endGameSession(gameSessionId, true);
            }
          } else {
            setView('round-summary');
          }
        }
      } catch (error) {
        setIsAIThinking(false);
        console.error('AI turn error:', error);
      }
    }, aiSpeedDelayRef.current);
  }, [ai, engine]);

  // Handle turn transition ready
  const handleTransitionReady = useCallback(() => {
    setView('playing');
  }, []);

  // Handle next round
  const handleNextRound = useCallback(() => {
    engine.startNewRound();
    setMoveCount(prev => prev + 1);
    setView('playing');
  }, [engine]);

  // Handle new game
  const handleNewGame = useCallback(() => {
    setSettings(null);
    setAi(null);
    setView('mode-select');
    setIsAIThinking(false);
    setMoveCount(0);
  }, []);

  // Handle exit
  const handleExit = useCallback(() => {
    if (gameSessionId) {
      GameSessionManager.getInstance().endGameSession(gameSessionId, false);
    }
    if (onExit) {
      onExit();
    }
  }, [onExit, gameSessionId]);

  // Generate default player names
  const generateDefaultNames = (count: number, mode: string): string[] => {
    if (mode === 'singleplayer') {
      return [user?.name || 'You', 'AI'];
    }
    return Array.from({ length: count }, (_, i) => `Player ${i + 1}`);
  };

  // Render based on current view
  const renderContent = () => {
    switch (view) {
      case 'mode-select':
        return (
          <GolfGameModeSelector
            onStartGame={handleStartGame}
            onJoinPrivateGame={handleJoinPrivateGame}
            guestGamesRemaining={isAuthenticated ? undefined : 3}
            isAuthenticated={isAuthenticated}
          />
        );

      case 'playing': {
        const gameState = engine.getGameState();
        const currentPlayerIndex = settings?.mode === 'local-multiplayer'
          ? gameState.currentPlayerIndex
          : 0;

        const layoutConfig: UniversalGameLayoutConfig = {
          gameName: 'Golf Card Game',
          board: (
            <GolfBoard
              engine={engine}
              playerIndex={currentPlayerIndex}
              interactive={!isAIThinking}
              isAIThinking={isAIThinking}
              onMove={handleMove}
              onInitialReveal={handleInitialReveal}
              showAllPlayers={settings?.mode === 'multiplayer-realtime'}
              animationSpeed={400}
            />
          ),
          leftPanels: [
            {
              id: 'scorecard',
              title: 'Scorecard',
              icon: '📊',
              content: (
                <Scorecard
                  scorecard={engine.getScorecard()}
                  currentRound={gameState.currentRound}
                  playerNames={gameState.playerNames}
                />
              ),
            },
          ],
          statusBar: {
            turnIndicator: (
              <span>Round {gameState.currentRound} of {gameState.totalRounds}</span>
            ),
            gameStatusType: 'active',
            aiThinking: isAIThinking,
          },
          actions: [
            { id: 'new-game', label: 'New Game', icon: '🔄', onClick: handleNewGame, variant: 'primary' },
            { id: 'exit', label: 'Exit', icon: '🚪', onClick: handleExit, variant: 'secondary' },
          ],
          secondaryActions: settings?.mode === 'singleplayer' ? [
            <AISpeedSlider key="ai-speed" value={aiSpeedDelay} onChange={handleAiSpeedChange} />,
          ] : undefined,
        };

        return <UniversalGameLayout config={layoutConfig} />;
      }

      case 'turn-transition': {
        const gameState = engine.getGameState();
        return (
          <TurnTransition
            nextPlayerName={gameState.playerNames[transitionPlayerIndex]}
            nextPlayerIndex={transitionPlayerIndex}
            onReady={handleTransitionReady}
          />
        );
      }

      case 'round-summary': {
        const gameState = engine.getGameState();
        const layoutConfig: UniversalGameLayoutConfig = {
          gameName: 'Golf Card Game',
          board: (
            <div className="golf-game__round-summary">
              <h2 className="golf-game__summary-title">
                Round {gameState.currentRound} Complete
              </h2>
              <Scorecard
                scorecard={engine.getScorecard()}
                currentRound={gameState.currentRound}
                playerNames={gameState.playerNames}
              />
              <button
                className="golf-game__next-round-btn"
                onClick={handleNextRound}
              >
                Next Round →
              </button>
            </div>
          ),
          statusBar: {
            turnIndicator: <span>Round {gameState.currentRound} Complete</span>,
            gameStatusType: 'ended',
          },
          actions: [
            { id: 'next-round', label: 'Next Round', icon: '➡️', onClick: handleNextRound, variant: 'primary' },
            { id: 'exit', label: 'Exit', icon: '🚪', onClick: handleExit, variant: 'secondary' },
          ],
        };
        return <UniversalGameLayout config={layoutConfig} />;
      }

      case 'game-over': {
        const gameState = engine.getGameState();
        const winner = engine.getWinner();
        const winnerNames = Array.isArray(winner)
          ? winner.map(i => gameState.playerNames[i]).join(' & ')
          : winner !== null
            ? gameState.playerNames[winner]
            : 'Unknown';
        const isHumanWinner = Array.isArray(winner) ? winner.includes(0) : winner === 0;

        const layoutConfig: UniversalGameLayoutConfig = {
          gameName: 'Golf Card Game',
          board: (
            <div className="golf-game__game-over">
              <Scorecard
                scorecard={engine.getScorecard()}
                currentRound={9}
                playerNames={gameState.playerNames}
                highlightWinner={true}
              />
            </div>
          ),
          statusBar: {
            turnIndicator: <span>Game Over</span>,
            gameStatus: 'Game Over',
            gameStatusType: 'ended',
          },
          actions: [
            { id: 'new-game', label: 'New Game', icon: '🔄', onClick: handleNewGame, variant: 'primary' },
            { id: 'exit', label: 'Exit', icon: '🚪', onClick: handleExit, variant: 'secondary' },
          ],
          gameOver: {
            show: true,
            result: isHumanWinner ? 'win' : 'loss',
            title: Array.isArray(winner) ? 'Tie!' : `${winnerNames} Wins!`,
            subtitle: Array.isArray(winner) ? `${winnerNames} tied for the win` : undefined,
            icon: '🏆',
            onPlayAgain: handleNewGame,
            onExit: handleExit,
          },
        };
        return <UniversalGameLayout config={layoutConfig} />;
      }

      default:
        return null;
    }
  };

  return (
    <div className="golf-game">
      {renderContent()}
    </div>
  );
};

// Wrap in ErrorBoundary
export const GolfGame: React.FC<GolfGameProps> = (props) => {
  return (
    <ErrorBoundary componentName="GolfCardGame">
      <GolfGameInner {...props} />
    </ErrorBoundary>
  );
};

export default GolfGame;
